import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  faAddressBook,
  faBolt,
  faBook,
  faChartLine as _faChartLine,
  faCog as _faCog,
  faMessages,
  faStethoscope,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";

// Import the Interactions component directly
import { Interactions } from "@perfectserve/interactions";

import ComingSoon from "./components/ComingSoon";
import ProtectedRoute from "./components/ProtectedRoute";
import { TransitionWrapper } from "./components/TransitionWrapper";
import { PERMISSIONS } from "./constants/permissions";
import AdminPage from "./pages/AdminPage";
import DocumentationPage from "./pages/DocumentationPage";
import HumanPage from "./pages/HumanPage";
import LandingPage from "./pages/LandingPage";
import { useAuth } from "./hooks";

// Authentication wrapper for routes that only need login check
const AuthenticatedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.2rem",
        }}
      >
        Loading...
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

const AppRouter = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <TransitionWrapper>
            <LandingPage />
          </TransitionWrapper>
        }
      />
      <Route
        path="/index.html"
        element={
          <TransitionWrapper>
            <LandingPage />
          </TransitionWrapper>
        }
      />
      <Route
        path="/messaging"
        element={
          <AuthenticatedRoute>
            <TransitionWrapper>
              <ComingSoon
                title="Messaging"
                description="Secure messaging and communication features will be available here."
                icon={faMessages}
              />
            </TransitionWrapper>
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/directory"
        element={
          <AuthenticatedRoute>
            <TransitionWrapper>
              <ComingSoon
                title="Directory"
                description="Contact directory and organizational features will be available here."
                icon={faAddressBook}
              />
            </TransitionWrapper>
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <AuthenticatedRoute>
            <TransitionWrapper>
              <ComingSoon
                title="Library"
                description="Document library and knowledge management features will be available here."
                icon={faBook}
              />
            </TransitionWrapper>
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/scheduling"
        element={
          <ProtectedRoute requirePerfectServeUser={true} feature="Scheduling">
            <TransitionWrapper>
              <ComingSoon
                title="Scheduling"
                description="Staff scheduling and shift management features will be available here."
                icon={faBolt}
              />
            </TransitionWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute requirePerfectServeUser={true} feature="Patients">
            <TransitionWrapper>
              <ComingSoon
                title="Patients"
                description="Patient management and care coordination features will be available here."
                icon={faStethoscope}
              />
            </TransitionWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interactions"
        element={
          <ProtectedRoute
            requirePerfectServeUser={true}
            requiredPermission={PERMISSIONS.CSP_AFTER_HOURS_REPORTING}
            feature="Interactions"
          >
            <TransitionWrapper>
              <Interactions />
            </TransitionWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/caremobility"
        element={
          <ProtectedRoute
            requirePerfectServeUser={true}
            requiredPermission={PERMISSIONS.HOSPITAL_PRACTITIONER_ADMINISTRATOR}
            feature="Care Mobility"
          >
            <TransitionWrapper>
              <ComingSoon
                title="Care Mobility"
                description="Patient mobility tracking and analytics features will be available here."
                icon={faTriangleExclamation}
              />
            </TransitionWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requirePerfectServeUser={true} feature="Admin Panel">
            <TransitionWrapper>
              <AdminPage />
            </TransitionWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation"
        element={
          <AuthenticatedRoute>
            <TransitionWrapper>
              <DocumentationPage />
            </TransitionWrapper>
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/human"
        element={
          <AuthenticatedRoute>
            <TransitionWrapper>
              <HumanPage />
            </TransitionWrapper>
          </AuthenticatedRoute>
        }
      />
    </Routes>
  );
};

export default AppRouter;
